import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Home, 
  Building, 
  Building2, 
  Store, 
  Star,
  MapPin,
  Edit,
  Trash2,
  Settings,
  CheckCircle,
  X
} from 'lucide-react';
import { api } from '../../services/api';
import toast from 'react-hot-toast';

const PropertyListPage = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [postalCodeError, setPostalCodeError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    category: 'RESIDENTIAL', // RESIDENTIAL or COMMERCIAL
    type: 'HDB',
    subType: '', // More specific property subtypes
    address: {
      street: '',
      unit: '',
      postalCode: ''
    },
    details: {
      size: '',
      sizeRange: '', // For size-based pricing
      bedrooms: '',
      bathrooms: '',
      yearBuilt: '',
      floorNumber: '',
      totalFloors: '',
      rooms: '', // For HDB room types
      builtUpArea: '', // For landed properties
      roomCount: '' // For hospitality
    },
    notes: ''
  });

  // Property type configurations based on your pricing structure
  const propertyConfig = {
    RESIDENTIAL: {
      HDB: {
        label: 'HDB',
        subtypes: [
          { value: '3_ROOMS_BELOW', label: '3 Rooms & Below', price: 25 },
          { value: '4_ROOMS', label: '4 Rooms HDB', price: 25 },
          { value: '5_ROOMS', label: '5 Rooms HDB', price: 30 },
          { value: 'EXECUTIVE_MAISONETTE', label: 'Executive Maisonette / 3Gen HDB / Jumbo Flat', price: 30 }
        ]
      },
      CONDOMINIUM: {
        label: 'Condominium / Walk Up Apartment / Executive Condominium',
        subtypes: [
          { value: 'UNDER_400', label: 'Under 400 sqft', price: 30 },
          { value: '401_600', label: '401-600 sqft', price: 35 },
          { value: '601_800', label: '601-800 sqft', price: 40 },
          { value: '801_1200', label: '801-1200 sqft', price: 45 },
          { value: '1201_1500', label: '1201-1500 sqft', price: 50 },
          { value: '1501_2000', label: '1501-2000 sqft', price: 55 },
          { value: '2001_4000', label: '2001-4000 sqft', price: 70 },
          { value: '4001_6000', label: '4001-6000 sqft', price: 100 },
          { value: '6001_ABOVE', label: '6001 sqft and above', price: 'Quote' }
        ]
      },
      LANDED: {
        label: 'Landed Property',
        subtypes: [
          { value: 'TERRACE_1500', label: 'Terrace House - 1500 sqft', price: 100 },
          { value: 'TERRACE_1500_2000', label: 'Terrace House - 1500-2000 sqft', price: 150 },
          { value: 'TERRACE_2001_3000', label: 'Terrace House - 2001-3000 sqft', price: 200 },
          { value: 'TERRACE_3001_ABOVE', label: 'Terrace House - 3001 sqft & Above', price: 250 },
          { value: 'SEMI_D_2000', label: 'Semi-Detached - 2000 sqft', price: 250 },
          { value: 'SEMI_D_2001_3500', label: 'Semi-Detached - 2001-3500 sqft', price: 250 },
          { value: 'SEMI_D_3501_5500', label: 'Semi-Detached - 3501-5500 sqft', price: 300 },
          { value: 'SEMI_D_5501_10000', label: 'Semi-Detached - 5501-10000 sqft', price: 400 },
          { value: 'SEMI_D_10000_ABOVE', label: 'Semi-Detached - 10000 sqft & Above', price: 'Quote' },
          { value: 'GCB', label: 'Good Class Bungalow (GCB)', price: 'Quote' }
        ]
      }
    },
    COMMERCIAL: {
      COMMERCIAL: {
        label: 'Commercial',
        subtypes: [
          { value: 'RETAIL', label: 'Retail', price: 50 },
          { value: 'FOOD_BEVERAGE', label: 'Food & Beverage', price: 50 },
          { value: 'COMMERCIAL_OTHERS', label: 'Others', price: 50 }
        ]
      },
      INDUSTRIAL: {
        label: 'Industrial',
        subtypes: [
          { value: 'INDUSTRIAL_OFFICE', label: 'Industrial Office', price: 50 },
          { value: 'STANDALONE_WAREHOUSE', label: 'Standalone Warehouse', price: 50 }
        ]
      },
      HOSPITALITY: {
        label: 'Hospitality (By Rooms)',
        subtypes: [
          { value: 'HOSPITALITY_20_35', label: '20-35 Rooms', price: 100 },
          { value: 'HOSPITALITY_35_50', label: '35-50 Rooms', price: 150 },
          { value: 'HOSPITALITY_51_99', label: '51-99 Rooms', price: 200 }
        ]
      },
      INSTITUTIONAL: {
        label: 'Institutional',
        subtypes: [
          { value: 'RELIGIOUS_INSTITUTIONAL', label: 'Religious/Institutional', price: 200 },
          { value: 'SPORTS_HALL', label: 'Sports Hall', price: 200 },
          { value: 'COMMUNITY_CENTRE', label: 'Community Centre', price: 200 }
        ]
      }
    }
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const response = await api.get('/properties');
      if (response.success) {
        setProperties(response.data);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const propertyIcons = {
    HDB: Home,
    CONDOMINIUM: Building,
    LANDED: Building2,
    COMMERCIAL: Store,
    INDUSTRIAL: Store,
    HOSPITALITY: Building,
    INSTITUTIONAL: Building
  };

  const propertyTypeColors = {
    HDB: 'bg-green-100 text-green-800 border-green-200',
    CONDOMINIUM: 'bg-blue-100 text-blue-800 border-blue-200',
    LANDED: 'bg-purple-100 text-purple-800 border-purple-200',
    COMMERCIAL: 'bg-orange-100 text-orange-800 border-orange-200',
    INDUSTRIAL: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    HOSPITALITY: 'bg-pink-100 text-pink-800 border-pink-200',
    INSTITUTIONAL: 'bg-gray-100 text-gray-800 border-gray-200'
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate postal code before submission
    const postalValidation = validatePostalCode(formData.address.postalCode);
    if (!postalValidation.isValid) {
      toast.error(postalValidation.error);
      setPostalCodeError(postalValidation.error);
      return;
    }
    
    try {
      let response;
      if (editingProperty) {
        response = await api.put(`/properties/${editingProperty._id}`, formData);
      } else {
        response = await api.post('/properties', formData);
      }

      if (response.success) {
        toast.success(editingProperty ? 'Property updated successfully' : 'Property created successfully');
        fetchProperties();
        closeModal();
      }
    } catch (error) {
      console.error('Error saving property:', error);
      toast.error(error.response?.data?.message || 'Failed to save property');
    }
  };

  const handleDelete = async (propertyId) => {
    if (!window.confirm('Are you sure you want to delete this property?')) {
      return;
    }

    try {
      const response = await api.delete(`/properties/${propertyId}`);
      if (response.success) {
        toast.success('Property deleted successfully');
        fetchProperties();
      }
    } catch (error) {
      console.error('Error deleting property:', error);
      toast.error(error.response?.data?.message || 'Failed to delete property');
    }
  };

  const handleSetDefault = async (propertyId) => {
    try {
      const response = await api.put(`/properties/${propertyId}/set-default`);
      if (response.success) {
        toast.success('Default property updated');
        fetchProperties();
      }
    } catch (error) {
      console.error('Error setting default property:', error);
      toast.error('Failed to set default property');
    }
  };

  const openEditModal = (property) => {
    setEditingProperty(property);
    setFormData({
      name: property.name,
      category: property.category || 'RESIDENTIAL',
      type: property.type,
      subType: property.subType || '',
      address: property.address,
      details: {
        size: property.details?.size || '',
        sizeRange: property.details?.sizeRange || '',
        bedrooms: property.details?.bedrooms || '',
        bathrooms: property.details?.bathrooms || '',
        yearBuilt: property.details?.yearBuilt || '',
        floorNumber: property.details?.floorNumber || '',
        totalFloors: property.details?.totalFloors || '',
        rooms: property.details?.rooms || '',
        builtUpArea: property.details?.builtUpArea || '',
        roomCount: property.details?.roomCount || ''
      },
      notes: property.notes || ''
    });
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingProperty(null);
    setPostalCodeError(''); // Clear postal code error
    setFormData({
      name: '',
      category: 'RESIDENTIAL',
      type: 'HDB',
      subType: '',
      address: {
        street: '',
        unit: '',
        postalCode: ''
      },
      details: {
        size: '',
        sizeRange: '',
        bedrooms: '',
        bathrooms: '',
        yearBuilt: '',
        floorNumber: '',
        totalFloors: '',
        rooms: '',
        builtUpArea: '',
        roomCount: ''
      },
      notes: ''
    });
  };

  // Get available property types based on category
  const getAvailableTypes = () => {
    return Object.keys(propertyConfig[formData.category] || {});
  };

  // Get available subtypes based on selected type
  const getAvailableSubtypes = () => {
    const typeConfig = propertyConfig[formData.category]?.[formData.type];
    return typeConfig?.subtypes || [];
  };

  // Validate postal code
  const validatePostalCode = (postalCode) => {
    // Check if it's exactly 6 digits
    if (!/^\d{6}$/.test(postalCode)) {
      return { isValid: false, error: 'Postal code must be exactly 6 digits' };
    }

    // Get first two digits
    const firstTwoDigits = parseInt(postalCode.substring(0, 2));

    // Check if it's in the allowed ranges (22-30 or 58-59)
    const isAllowed = (firstTwoDigits >= 22 && firstTwoDigits <= 30) || 
                      (firstTwoDigits >= 58 && firstTwoDigits <= 59);

    if (!isAllowed) {
      return { isValid: false, error: 'Service is not available in this district' };
    }

    return { isValid: true, error: '' };
  };

  // Handle postal code change
  const handlePostalCodeChange = (value) => {
    setFormData({
      ...formData,
      address: { ...formData.address, postalCode: value }
    });

    if (value) {
      const validation = validatePostalCode(value);
      setPostalCodeError(validation.error);
    } else {
      setPostalCodeError('');
    }
  };

  // Handle opening the add property modal
  const handleAddPropertyClick = () => {
    // Show service availability alert
    alert('The service is available in District 9, 10, 11, 21');
    setShowAddModal(true);
  };

  // Handle category change
  const handleCategoryChange = (category) => {
    const availableTypes = Object.keys(propertyConfig[category] || {});
    const firstType = availableTypes[0] || '';
    setFormData({
      ...formData,
      category,
      type: firstType,
      subType: ''
    });
  };

  // Handle type change
  const handleTypeChange = (type) => {
    setFormData({
      ...formData,
      type,
      subType: ''
    });
  };

  const PropertyCard = ({ property }) => {
    const IconComponent = propertyIcons[property.type] || Home;
    const hasSubscription = property.subscription;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center">
              <div className="p-3 bg-orange-100 rounded-lg mr-3">
                <IconComponent className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  {property.name}
                  {property.isDefault && (
                    <Star className="h-4 w-4 text-yellow-500 ml-2 fill-current" />
                  )}
                </h3>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${propertyTypeColors[property.type]}`}>
                  {property.type}
                </span>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => openEditModal(property)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Edit className="h-4 w-4" />
              </button>
              {!hasSubscription && (
                <button
                  onClick={() => handleDelete(property._id)}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Address */}
          <div className="flex items-start mb-4">
            <MapPin className="h-4 w-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
            <p className="text-sm text-gray-600">{property.fullAddress}</p>
          </div>

          {/* Subscription Status */}
          <div className="mb-4">
            {hasSubscription ? (
              <div className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                <div>
                  <p className="text-sm font-medium text-green-900">Active Subscription</p>
                  <p className="text-xs text-green-700">
                    {property.subscription.billingCycle} billing - ${property.subscription.currentPrice}
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-sm text-gray-600">No active subscription</p>
                <button className="text-sm text-orange-600 hover:text-orange-700 font-medium mt-1">
                  Subscribe to a plan →
                </button>
              </div>
            )}
          </div>

          {/* Property Details */}
          {property.details && Object.keys(property.details).length > 0 && (
            <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-4">
              {property.details.size && (
                <div>
                  <span className="font-medium">Size:</span> {property.details.size} sq ft
                </div>
              )}
              {property.details.bedrooms && (
                <div>
                  <span className="font-medium">Bedrooms:</span> {property.details.bedrooms}
                </div>
              )}
              {property.details.bathrooms && (
                <div>
                  <span className="font-medium">Bathrooms:</span> {property.details.bathrooms}
                </div>
              )}
              {property.details.yearBuilt && (
                <div>
                  <span className="font-medium">Built:</span> {property.details.yearBuilt}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-between items-center pt-4 border-t border-gray-100">
            {!property.isDefault && (
              <button
                onClick={() => handleSetDefault(property._id)}
                className="text-sm text-orange-600 hover:text-orange-700 font-medium"
              >
                Set as Default
              </button>
            )}
            {property.isDefault && (
              <span className="text-sm text-gray-500 font-medium">Default Property</span>
            )}
            
            <button className="flex items-center text-sm text-gray-600 hover:text-gray-700">
              <Settings className="h-4 w-4 mr-1" />
              Manage
            </button>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 -mt-20">
      {/* Modern Hero Section - Matching Other Pages */}
      <section className="relative overflow-hidden">
        {/* Background with geometric patterns */}
        <div className="absolute inset-0 bg-orange-600">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700"></div>
          {/* Geometric shapes */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500 rounded-full opacity-20 transform translate-x-32 -translate-y-32"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-orange-700 rounded-full opacity-30 transform -translate-x-24 translate-y-24"></div>
          <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-white opacity-5 rounded-full"></div>
          <div className="absolute top-1/4 right-1/3 w-48 h-48 bg-orange-400 rounded-full opacity-10 transform rotate-45"></div>
        </div>

        <div className="relative container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto text-center text-white">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="mb-6"
            >
              <span className="inline-block px-4 py-2 bg-orange-500 bg-opacity-30 rounded-full text-orange-100 text-sm font-medium mb-4 backdrop-blur-sm">
                Property Management
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="text-4xl md:text-5xl font-bold mb-6 leading-tight"
            >
              My Properties
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-xl text-orange-100 max-w-3xl mx-auto mb-10 leading-relaxed"
            >
              Manage your properties and their maintenance subscriptions
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              <button
                onClick={handleAddPropertyClick}
                className="inline-flex items-center px-8 py-4 bg-white text-orange-600 rounded-xl hover:bg-gray-100 transition-all duration-300 font-semibold text-lg shadow-lg hover:shadow-xl"
              >
                <Plus className="h-6 w-6 mr-2" />
                Add New Property
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* Properties Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
                <div className="h-20 bg-gray-200 rounded mb-4"></div>
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </div>
            ))}
          </div>
        ) : properties.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {properties.map((property) => (
              <PropertyCard key={property._id} property={property} />
            ))}
          </div>
        ) : (
          <div className="max-w-md mx-auto">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-12 text-center">
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Home className="h-10 w-10 text-orange-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">No Properties Yet</h3>
              <p className="text-gray-600 mb-8 leading-relaxed">Add your first property to get started with maintenance subscriptions</p>
              <button
                onClick={handleAddPropertyClick}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Your First Property
              </button>
            </div>
          </div>
        )}

        {/* Add/Edit Property Modal */}
        <AnimatePresence>
          {showAddModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              >
                <form onSubmit={handleSubmit} className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-gray-900">
                      {editingProperty ? 'Edit Property' : 'Add New Property'}
                    </h3>
                    <button
                      type="button"
                      onClick={closeModal}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-6">
                    {/* Basic Information */}
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Property Name *
                          </label>
                          <input
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            placeholder="e.g., My Home, Office Building"
                          />
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Property Category *
                            </label>
                            <select
                              required
                              value={formData.category}
                              onChange={(e) => handleCategoryChange(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            >
                              <option value="RESIDENTIAL">Residential</option>
                              <option value="COMMERCIAL">Commercial</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Property Type *
                            </label>
                            <select
                              required
                              value={formData.type}
                              onChange={(e) => handleTypeChange(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            >
                              {getAvailableTypes().map((type) => (
                                <option key={type} value={type}>
                                  {propertyConfig[formData.category][type].label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Specific Property Details *
                          </label>
                          <select
                            required
                            value={formData.subType}
                            onChange={(e) => setFormData({ ...formData, subType: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          >
                            <option value="">Select specific property details</option>
                            {getAvailableSubtypes().map((subtype) => (
                              <option key={subtype.value} value={subtype.value}>
                                {subtype.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Address */}
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 mb-4">Address</h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Street Address *
                          </label>
                          <input
                            type="text"
                            required
                            value={formData.address.street}
                            onChange={(e) => setFormData({
                              ...formData,
                              address: { ...formData.address, street: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            placeholder="Street address"
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Unit Number
                            </label>
                            <input
                              type="text"
                              value={formData.address.unit}
                              onChange={(e) => setFormData({
                                ...formData,
                                address: { ...formData.address, unit: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                              placeholder="e.g., 12-34"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Postal Code *
                            </label>
                            <input
                              type="text"
                              required
                              maxLength="6"
                              value={formData.address.postalCode}
                              onChange={(e) => handlePostalCodeChange(e.target.value)}
                              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                                postalCodeError ? 'border-red-300' : 'border-gray-300'
                              }`}
                              placeholder="123456"
                            />
                            {postalCodeError && (
                              <p className="mt-1 text-sm text-red-600">{postalCodeError}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Property Details */}
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 mb-4">Additional Details (Optional)</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Size field - shown for most property types */}
                        {(formData.type === 'CONDOMINIUM' || formData.type === 'LANDED') && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Built-up Area (sq ft)
                            </label>
                            <input
                              type="number"
                              value={formData.details.size}
                              onChange={(e) => setFormData({
                                ...formData,
                                details: { ...formData.details, size: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                              placeholder="e.g., 1200"
                            />
                          </div>
                        )}

                        {/* Room count for HDB */}
                        {formData.type === 'HDB' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Number of Rooms
                            </label>
                            <select
                              value={formData.details.rooms}
                              onChange={(e) => setFormData({
                                ...formData,
                                details: { ...formData.details, rooms: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            >
                              <option value="">Select number of rooms</option>
                              <option value="1">1 Room</option>
                              <option value="2">2 Rooms</option>
                              <option value="3">3 Rooms</option>
                              <option value="4">4 Rooms</option>
                              <option value="5">5 Rooms</option>
                              <option value="executive">Executive</option>
                            </select>
                          </div>
                        )}

                        {/* Bedrooms - for residential properties */}
                        {formData.category === 'RESIDENTIAL' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Bedrooms
                            </label>
                            <input
                              type="number"
                              value={formData.details.bedrooms}
                              onChange={(e) => setFormData({
                                ...formData,
                                details: { ...formData.details, bedrooms: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                              min="0"
                              max="10"
                            />
                          </div>
                        )}

                        {/* Bathrooms - for residential properties */}
                        {formData.category === 'RESIDENTIAL' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Bathrooms
                            </label>
                            <input
                              type="number"
                              value={formData.details.bathrooms}
                              onChange={(e) => setFormData({
                                ...formData,
                                details: { ...formData.details, bathrooms: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                              min="0"
                              max="10"
                            />
                          </div>
                        )}

                        {/* Floor details - for HDB and Condominiums */}
                        {(formData.type === 'HDB' || formData.type === 'CONDOMINIUM') && (
                          <>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Floor Number
                              </label>
                              <input
                                type="number"
                                value={formData.details.floorNumber}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  details: { ...formData.details, floorNumber: e.target.value }
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                min="1"
                                max="100"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Total Floors in Building
                              </label>
                              <input
                                type="number"
                                value={formData.details.totalFloors}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  details: { ...formData.details, totalFloors: e.target.value }
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                min="1"
                                max="100"
                              />
                            </div>
                          </>
                        )}

                        {/* Room count for hospitality */}
                        {formData.type === 'HOSPITALITY' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Number of Rooms
                            </label>
                            <input
                              type="number"
                              value={formData.details.roomCount}
                              onChange={(e) => setFormData({
                                ...formData,
                                details: { ...formData.details, roomCount: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                              min="1"
                              placeholder="Number of hotel/accommodation rooms"
                            />
                          </div>
                        )}

                        {/* Year Built - for all properties */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Year Built
                          </label>
                          <input
                            type="number"
                            value={formData.details.yearBuilt}
                            onChange={(e) => setFormData({
                              ...formData,
                              details: { ...formData.details, yearBuilt: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            min="1800"
                            max={new Date().getFullYear() + 5}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Notes */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Notes
                      </label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Any additional notes about this property..."
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
                    >
                      {editingProperty ? 'Update Property' : 'Add Property'}
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PropertyListPage;